/**
 * constants.js — SINGLE SOURCE OF TRUTH for all Carnival Chaos tuning data.
 *
 * Every number in this file traces back to a table in
 * Designs/Carnival_Chaos_GDD_v0.7.md. Section references are noted inline.
 * Gameplay code must read from here rather than hardcoding values, so that
 * design changes happen in exactly one place.
 *
 * NOTHING in this module imports anything. It is pure data plus a handful of
 * pure helper functions, which makes it safe to import from any other module
 * without creating a cycle.
 *
 * @module constants
 */

export const GAME_VERSION = '2.0.0';
export const GDD_VERSION = '0.7';

// ─────────────────────────────────────────────────────────────────────────────
// Engine / render
// ─────────────────────────────────────────────────────────────────────────────

/** Physics runs at the Quest 3S display rate (GDD 12.2: 72 Hz target). */
export const PHYSICS_HZ = 72;
export const PHYSICS_DT = 1 / PHYSICS_HZ;

/** Frame delta is clamped before accumulation so a stalled tab cannot spiral. */
export const MAX_FRAME_DT = 0.1;
/** Hard ceiling on physics substeps per rendered frame. */
export const MAX_SUBSTEPS = 5;

/** Device pixel ratio ceiling — beyond 2 costs fill rate for no visible gain. */
export const MAX_PIXEL_RATIO = 2;

export const DEFAULT_GRAVITY = 9.81;

/** GDD 12.2 performance budgets (Quest 3S), used by the perf overlay. */
export const PERF_BUDGET = {
  frameRateTarget: 72,
  drawCallsPerEye: 100,
  drawCallsMax: 150,
  trianglesPerEye: 500_000,
  trianglesMax: 750_000,
  activeRigidBodiesPerStall: 50,
  activeRigidBodiesMax: 100,
};

// ─────────────────────────────────────────────────────────────────────────────
// Physics collision groups (GDD 10.4 — griefing prevention)
//
// Rapier packs interaction groups into a u32: the high 16 bits are the
// membership bitmask, the low 16 bits are the filter bitmask. Two colliders
// interact only if each one's membership intersects the other's filter.
//
// The load-bearing rule: REMOTE_COSMETIC never collides with anything. Other
// players' throws are replayed animations, not simulated bodies, so they can
// never touch a local player's targets.
// ─────────────────────────────────────────────────────────────────────────────

export const GROUP = {
  /** Ground, booth structure, walls — never moves. */
  STATIC: 0x0001,
  /** The local player's balls, cans, coconuts, rings… fully simulated. */
  LOCAL_DYNAMIC: 0x0002,
  /** Remote players' replayed objects. Simulated by nobody, collides with nothing. */
  REMOTE_COSMETIC: 0x0004,
  /** The local player's hands / wing colliders. */
  HAND: 0x0008,
  /** Trigger volumes: scoring zones, approach zones, off-shelf detectors. */
  SENSOR: 0x0010,
};

/**
 * Pack a membership mask and a filter mask into a Rapier interaction group u32.
 * @param {number} membership Bitwise-OR of GROUP values this collider belongs to.
 * @param {number} filter Bitwise-OR of GROUP values this collider may touch.
 * @returns {number} u32 interaction groups value.
 */
export function makeGroups(membership, filter) {
  return (((membership & 0xffff) << 16) | (filter & 0xffff)) >>> 0;
}

/** Ready-made interaction groups for the common collider roles. */
export const COLLISION = {
  STATIC: makeGroups(GROUP.STATIC, GROUP.LOCAL_DYNAMIC | GROUP.HAND | GROUP.SENSOR),
  LOCAL_DYNAMIC: makeGroups(
    GROUP.LOCAL_DYNAMIC,
    GROUP.STATIC | GROUP.LOCAL_DYNAMIC | GROUP.HAND | GROUP.SENSOR,
  ),
  /** Membership only — an empty filter means "collide with nothing". */
  REMOTE_COSMETIC: makeGroups(GROUP.REMOTE_COSMETIC, 0),
  HAND: makeGroups(GROUP.HAND, GROUP.STATIC | GROUP.LOCAL_DYNAMIC | GROUP.SENSOR),
  SENSOR: makeGroups(GROUP.SENSOR, GROUP.LOCAL_DYNAMIC | GROUP.HAND),
};

// ─────────────────────────────────────────────────────────────────────────────
// Throw physics defaults (GDD 5.3)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Baseline velocity-estimator settings. Per-stall profiles below override
 * individual fields; anything they omit falls back to these.
 *
 * @typedef {Object} ThrowProfile
 * @property {number} velocitySmoothFrames Frames in the rolling average (3–12).
 * @property {number} velocityWeightDecay  Exponential recency weight per frame back.
 * @property {number} releaseFrameOffset   Frames before release to stop sampling (0–3).
 * @property {number} minThrowSpeed        m/s — below this the object drops limply.
 * @property {number} maxThrowSpeed        m/s — hard clamp on release speed.
 * @property {number} aimAssistCone        Degrees of correction cone (0 disables).
 * @property {number} aimAssistStrength    0–0.3 fraction of the angle corrected.
 * @property {number} gravityScale         Per-object gravity multiplier (0.5–2.0).
 */
export const DEFAULT_THROW_PROFILE = Object.freeze({
  velocitySmoothFrames: 6,
  velocityWeightDecay: 0.72,
  releaseFrameOffset: 1,
  minThrowSpeed: 1.5,
  maxThrowSpeed: 20,
  aimAssistCone: 5,
  aimAssistStrength: 0.15,
  gravityScale: 1.0,
});

/** Ring-buffer capacity for hand pose samples — must exceed max smoothFrames. */
export const VELOCITY_BUFFER_SIZE = 16;

/**
 * Per-stall throw profiles (GDD 5.4).
 * Stalls with no thrown projectile (shooting, mallets, rod, coin drop) still
 * declare a profile so grab.js has consistent clamps for held objects.
 */
export const THROW_PROFILES = Object.freeze({
  'tin-can-alley': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 5, aimAssistStrength: 0.12, gravityScale: 1.0, maxThrowSpeed: 18,
    minThrowSpeed: 1.5,
  },
  'coconut-shy': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 0, aimAssistStrength: 0, gravityScale: 1.2, maxThrowSpeed: 20,
    minThrowSpeed: 2.0,
  },
  'ring-toss': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 10, aimAssistStrength: 0.2, gravityScale: 0.8, maxThrowSpeed: 12,
    minThrowSpeed: 1.0,
  },
  'ping-pong-goldfish': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 5, aimAssistStrength: 0.12, gravityScale: 0.6, maxThrowSpeed: 10,
    minThrowSpeed: 1.0,
  },
  'basketball-shoot': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 5, aimAssistStrength: 0.12, gravityScale: 1.0, maxThrowSpeed: 16,
    minThrowSpeed: 1.5,
  },
  'skee-ball': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 8, aimAssistStrength: 0.18, gravityScale: 1.0, maxThrowSpeed: 14,
    minThrowSpeed: 1.0,
  },
  'duck-gallery': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 0, aimAssistStrength: 0, gravityScale: 1.0, maxThrowSpeed: 8,
  },
  'dart-balloons': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 5, aimAssistStrength: 0.12, gravityScale: 0.7, maxThrowSpeed: 14,
    minThrowSpeed: 1.0, velocitySmoothFrames: 4, // wrist flick — shorter window
  },
  'high-striker': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 0, aimAssistStrength: 0, gravityScale: 1.0, maxThrowSpeed: 25,
  },
  'whack-a-mole': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 0, aimAssistStrength: 0, gravityScale: 1.0, maxThrowSpeed: 25,
  },
  'hook-a-duck': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 0, aimAssistStrength: 0, gravityScale: 1.0, maxThrowSpeed: 8,
  },
  'coin-pusher': {
    ...DEFAULT_THROW_PROFILE,
    aimAssistCone: 0, aimAssistStrength: 0, gravityScale: 1.0, maxThrowSpeed: 6,
    minThrowSpeed: 0.2, // coins are dropped, not thrown — GDD 5.4
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Haptics (GDD 5.5)
// ─────────────────────────────────────────────────────────────────────────────

export const HAPTIC_PATTERNS = Object.freeze({
  grab:    { intensity: 0.55, duration: 10 },
  release: { intensity: 0.35, duration: 5 },
  /** Impact intensity is scaled by normalised impact force at call time. */
  impact:  { intensity: 0.9,  duration: 30 },
  miss:    { intensity: 0.3,  duration: 15 },
  coin:    { intensity: 0.25, duration: 5 },
  /** Continuous low rumble while holding something heavy. */
  hold:    { intensity: 0.12, duration: 90, loop: true },
  ui:      { intensity: 0.2,  duration: 8 },
  score:   { intensity: 0.5,  duration: 18 },
  teleport:{ intensity: 0.3,  duration: 12 },
});

/** Impact force (newtons) that maps to full haptic intensity. */
export const HAPTIC_IMPACT_FULL_SCALE_N = 30;

// ─────────────────────────────────────────────────────────────────────────────
// Economy (GDD 8)
// ─────────────────────────────────────────────────────────────────────────────

/** GDD 8.3 — the Starter pack bundled with the $4.99 purchase. */
export const STARTING_TICKETS = 500;
export const STARTING_TOKENS = 0;
export const STARTING_STARS = 0;

/** GDD 8.3 — IAP ticket packs. */
export const TICKET_PACKS = Object.freeze([
  { id: 'starter',   name: 'Starter',     price: 4.99,  tickets: 500,  bonusPct: 0,  included: true },
  { id: 'quick',     name: 'Quick Top-Up', price: 1.99, tickets: 200,  bonusPct: 0 },
  { id: 'roll',      name: 'Ticket Roll',  price: 4.99, tickets: 550,  bonusPct: 10 },
  { id: 'bundle',    name: 'Ticket Bundle', price: 9.99, tickets: 1200, bonusPct: 20 },
  { id: 'golden',    name: 'Golden Roll',  price: 19.99, tickets: 2600, bonusPct: 30 },
]);

/** GDD 7.2 — the only two ticket price points (plus the premium Coin Pusher). */
export const TICKET_COST = Object.freeze({ EASY: 3, HARD: 5, PREMIUM: 5 });

/** GDD 8.7 — 30-day login streak calendar. Index 0 is unused; day 1..30. */
export const DAILY_LOGIN_CALENDAR = Object.freeze([
  null,
  { day: 1,  tickets: 5,  stars: 0 },
  { day: 2,  tickets: 5,  stars: 0 },
  { day: 3,  tickets: 5,  stars: 0 },
  { day: 4,  tickets: 5,  stars: 0 },
  { day: 5,  tickets: 5,  stars: 0 },
  { day: 6,  tickets: 5,  stars: 0 },
  { day: 7,  tickets: 15, stars: 1, milestone: 'weekly' },
  { day: 8,  tickets: 8,  stars: 0 },
  { day: 9,  tickets: 8,  stars: 0 },
  { day: 10, tickets: 8,  stars: 0 },
  { day: 11, tickets: 8,  stars: 0 },
  { day: 12, tickets: 8,  stars: 0 },
  { day: 13, tickets: 8,  stars: 0 },
  { day: 14, tickets: 20, stars: 1, milestone: 'fortnight' },
  { day: 15, tickets: 8,  stars: 0 },
  { day: 16, tickets: 8,  stars: 0 },
  { day: 17, tickets: 8,  stars: 0 },
  { day: 18, tickets: 8,  stars: 0 },
  { day: 19, tickets: 8,  stars: 0 },
  { day: 20, tickets: 8,  stars: 0 },
  { day: 21, tickets: 25, stars: 2, milestone: 'three-week' },
  { day: 22, tickets: 10, stars: 0 },
  { day: 23, tickets: 10, stars: 0 },
  { day: 24, tickets: 10, stars: 0 },
  { day: 25, tickets: 10, stars: 0 },
  { day: 26, tickets: 10, stars: 0 },
  { day: 27, tickets: 10, stars: 0 },
  { day: 28, tickets: 10, stars: 0 },
  { day: 29, tickets: 10, stars: 0 },
  { day: 30, tickets: 50, stars: 5, milestone: 'monthly', cosmetic: true },
]);

/** GDD 8.5 — themed prize sets. The meta agent owns the per-piece catalog. */
export const PRIZE_SETS = Object.freeze([
  { id: 'carnival-classic', name: 'Carnival Classic', pieces: 5, totalTokens: 500,  cosmetic: 'carnival-barker-hat', availability: 'permanent' },
  { id: 'neon-nights',      name: 'Neon Nights',      pieces: 6, totalTokens: 900,  cosmetic: 'neon-goose-glow',     availability: 'permanent' },
  { id: 'big-top-circus',   name: 'Big Top Circus',   pieces: 6, totalTokens: 900,  cosmetic: 'ringmaster-outfit',   availability: 'permanent' },
  { id: 'plush-paradise',   name: 'Plush Paradise',   pieces: 8, totalTokens: 1500, cosmetic: 'giant-plush-goose',   availability: 'permanent' },
  { id: 'golden-goose',     name: 'Golden Goose',     pieces: 8, totalTokens: 2500, cosmetic: 'golden-goose-skin',   availability: 'permanent' },
  { id: 'spooky-carnival',  name: 'Spooky Carnival',  pieces: 6, totalTokens: 1000, cosmetic: 'haunted-goose-skin',  availability: 'seasonal', season: 'Q4' },
  { id: 'winter-wonderland',name: 'Winter Wonderland',pieces: 6, totalTokens: 1000, cosmetic: 'snow-goose-skin',     availability: 'seasonal', season: 'Q1' },
  { id: 'summer-splash',    name: 'Summer Splash',    pieces: 6, totalTokens: 1000, cosmetic: 'beach-goose-skin',    availability: 'seasonal', season: 'Q3' },
]);

// ─────────────────────────────────────────────────────────────────────────────
// Token payout curves (GDD 7.3)
//
// Each stall maps a raw score onto a token payout. The GDD gives a score range
// and an "Avg–Max" token range per stall; we treat the low end of each pair as
// the payout at the bottom of the score band and the high end as the payout at
// the top, interpolating between them.
//
// Rules (implemented in computeTokens below):
//   score <= 0        → 0 tokens  (you played, you got nothing)
//   0 < score <= min  → tokenMin
//   score >= max      → tokenMax
//   otherwise         → interpolated along `curve`
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} TokenCurve
 * @property {number} scoreMin  Bottom of the GDD score range.
 * @property {number} scoreMax  Top of the GDD score range.
 * @property {number} tokenMin  Tokens at scoreMin ("Avg" column).
 * @property {number} tokenMax  Tokens at scoreMax ("Max" column).
 * @property {'linear'|'ease'} [curve] Interpolation shape. Default 'linear'.
 */
export const TOKEN_CURVES = Object.freeze({
  'tin-can-alley':      { scoreMin: 30,  scoreMax: 690,  tokenMin: 5,  tokenMax: 46 },
  'coconut-shy':        { scoreMin: 0,   scoreMax: 600,  tokenMin: 8,  tokenMax: 60 },
  'ring-toss':          { scoreMin: 0,   scoreMax: 2500, tokenMin: 8,  tokenMax: 65 },
  'ping-pong-goldfish': { scoreMin: 0,   scoreMax: 2500, tokenMin: 8,  tokenMax: 65 },
  'basketball-shoot':   { scoreMin: 0,   scoreMax: 3000, tokenMin: 8,  tokenMax: 75 },
  'skee-ball':          { scoreMin: 100, scoreMax: 5000, tokenMin: 5,  tokenMax: 50 },
  'duck-gallery':       { scoreMin: 200, scoreMax: 5000, tokenMin: 10, tokenMax: 80 },
  'dart-balloons':      { scoreMin: 50,  scoreMax: 2500, tokenMin: 5,  tokenMax: 50 },
  'high-striker':       { scoreMin: 0,   scoreMax: 1000, tokenMin: 8,  tokenMax: 60 },
  'whack-a-mole':       { scoreMin: 200, scoreMax: 4000, tokenMin: 5,  tokenMax: 50 },
  'hook-a-duck':        { scoreMin: 0,   scoreMax: 2500, tokenMin: 5,  tokenMax: 50 },
  /** Coin Pusher scores in coins-off-edge, not points (GDD 6.7.2). */
  'coin-pusher':        { scoreMin: 0,   scoreMax: 100,  tokenMin: 0,  tokenMax: 100 },
});

/**
 * Convert a round score into a redemption token payout.
 * Deterministic and pure — tests assert against this directly.
 *
 * @param {string} stallId
 * @param {number} score
 * @returns {number} Whole tokens awarded.
 */
export function computeTokens(stallId, score) {
  const c = TOKEN_CURVES[stallId];
  if (!c) return 0;
  if (!Number.isFinite(score) || score <= 0) return 0;
  if (score <= c.scoreMin) return c.tokenMin;
  if (score >= c.scoreMax) return c.tokenMax;
  const span = c.scoreMax - c.scoreMin;
  let t = span > 0 ? (score - c.scoreMin) / span : 1;
  if (c.curve === 'ease') t = t * t * (3 - 2 * t);
  return Math.round(c.tokenMin + (c.tokenMax - c.tokenMin) * t);
}

// ─────────────────────────────────────────────────────────────────────────────
// Stall registry data (GDD 6, 7.2, 10.2)
//
// This is the authoritative list of the 12 launch stalls. `row` and `slot`
// place the booth in the hub; `implemented` flips to true when a stall agent
// lands its module, which is what makes the hub render a live booth instead of
// a COMING SOON sign.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} StallSpec
 * @property {string} id
 * @property {string} name
 * @property {number} cost Tickets per round.
 * @property {'easy'|'hard'|'premium'} tier
 * @property {'left'|'right'} row
 * @property {number} slot 0–5, position along the midway.
 * @property {string} color Booth accent colour (hex).
 * @property {string} blurb One-line barker pitch for the sign.
 */
export const STALLS = Object.freeze([
  // Left row (GDD 10.2)
  { id: 'tin-can-alley',      name: 'Tin Can Alley',      cost: 3, tier: 'easy',    row: 'left',  slot: 0, color: '#e8503a', blurb: 'Knock \'em all down!' },
  { id: 'coconut-shy',        name: 'Coconut Shy',        cost: 5, tier: 'hard',    row: 'left',  slot: 1, color: '#8d5524', blurb: 'Real force required.' },
  { id: 'ring-toss',          name: 'Ring Toss',          cost: 5, tier: 'hard',    row: 'left',  slot: 2, color: '#3aa6e8', blurb: 'Ring a bottle, win a plush.' },
  { id: 'ping-pong-goldfish', name: 'Ping Pong Goldfish', cost: 5, tier: 'hard',    row: 'left',  slot: 3, color: '#ff8c42', blurb: 'Bounce it in, win a fish.' },
  { id: 'basketball-shoot',   name: 'Basketball Shoot',   cost: 5, tier: 'hard',    row: 'left',  slot: 4, color: '#f2762e', blurb: 'Swish the far hoop.' },
  { id: 'skee-ball',          name: 'Skee-Ball',          cost: 3, tier: 'easy',    row: 'left',  slot: 5, color: '#5ac18e', blurb: 'Roll for the jackpot.' },
  // Right row (GDD 10.2)
  { id: 'duck-gallery',       name: 'Duck Gallery',       cost: 5, tier: 'hard',    row: 'right', slot: 0, color: '#c94f7c', blurb: 'Cork rifle. Golden duck.' },
  { id: 'dart-balloons',      name: 'Dart Balloons',      cost: 3, tier: 'easy',    row: 'right', slot: 1, color: '#9b5de5', blurb: 'Pop the lot.' },
  { id: 'high-striker',       name: 'High Striker',       cost: 5, tier: 'hard',    row: 'right', slot: 2, color: '#e8b23a', blurb: 'RING THE BELL!' },
  { id: 'whack-a-mole',       name: 'Whack-a-Mole',       cost: 3, tier: 'easy',    row: 'right', slot: 3, color: '#7bc043', blurb: 'Two mallets. Sixty seconds.' },
  { id: 'hook-a-duck',        name: 'Hook a Duck',        cost: 3, tier: 'easy',    row: 'right', slot: 4, color: '#43b5c0', blurb: 'Always wins a fish.' },
  { id: 'coin-pusher',        name: 'Coin Pusher',        cost: 5, tier: 'premium', row: 'right', slot: 5, color: '#d4af37', blurb: 'Push \'em over the edge.' },
]);

/** Fast lookup by id. */
export const STALL_BY_ID = Object.freeze(
  Object.fromEntries(STALLS.map((s) => [s.id, s])),
);

/** All valid stall ids, in hub order. */
export const STALL_IDS = Object.freeze(STALLS.map((s) => s.id));

// ─────────────────────────────────────────────────────────────────────────────
// Hub layout (GDD 10.2)
//
// The midway runs down -Z. The player spawns at the entrance gate facing -Z,
// booths line both sides of a central walkway. Spacing is tuned so twelve
// booths read as a real midway while every teleport hop stays under ~7 m.
// ─────────────────────────────────────────────────────────────────────────────

export const HUB = Object.freeze({
  /** Spawn / entrance gate. */
  spawn: { x: 0, y: 0, z: 9 },
  spawnYaw: Math.PI, // facing -Z, down the midway

  /** Half-width of the walkway; booths sit just beyond this. */
  walkwayHalfWidth: 4.0,
  /** X position of each booth's play origin (where the player stands). */
  playOriginX: 4.0,
  /**
   * X position of the booth's FRONT face. Only 0.7 m beyond the play origin,
   * so the player stands at the counter — anything further and a stall's
   * targets end up outside its own booth.
   */
  boothX: 4.7,

  /** Z of the first booth slot and the gap between consecutive slots. */
  firstSlotZ: 3.0,
  slotSpacing: 6.5,

  boothWidth: 5.0,   // along the midway (Z)
  boothDepth: 6.5,   // into the row (X)
  boothHeight: 3.6,

  /** End-of-midway landmarks. */
  prizeCounter: { x: -3.4, y: 0, z: -38.5 },
  personalSpacePortal: { x: 3.4, y: 0, z: -38.5 },
  leaderboards: [
    { x: -4.6, y: 0, z: 6.0, yaw: Math.PI * 0.5 },
    { x: 4.6, y: 0, z: 6.0, yaw: -Math.PI * 0.5 },
  ],

  groundSize: 120,
  /** Radius within which the approach prompt for a stall appears. */
  approachRadius: 3.2,
});

/**
 * World-space position of a stall's play origin — the spot the player stands
 * at, from which the stall's local +Z points into the booth.
 * @param {StallSpec} spec
 * @returns {{ x: number, y: number, z: number, yaw: number }}
 */
export function stallPlayOrigin(spec) {
  const sign = spec.row === 'left' ? -1 : 1;
  return {
    x: HUB.playOriginX * sign,
    y: 0,
    z: HUB.firstSlotZ - spec.slot * HUB.slotSpacing,
    // Local +Z must point away from the walkway, into the booth.
    yaw: sign < 0 ? -Math.PI / 2 : Math.PI / 2,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Avatar & comfort (GDD 3, 12.3)
// ─────────────────────────────────────────────────────────────────────────────

export const AVATAR = Object.freeze({
  gooseHeight: 0.95,
  gooseRadius: 0.3,
  /** Desktop waddle speed, m/s. */
  moveSpeed: 3.2,
  turnSpeed: 2.6,
  /** Over-shoulder follow camera (GDD 3.2). */
  followOffset: { x: 0, y: 2.0, z: 3.6 },
  followLerp: 6.0,
  followLookHeight: 1.0,
  /** Eye height when standing at a stall in first person. */
  eyeHeight: 1.62,
  /** Waddle bob amplitude/frequency. */
  bobAmplitude: 0.055,
  bobFrequency: 7.5,
  /** Seconds for the third↔first person fade (GDD 3.3). */
  modeTransitionTime: 0.35,
});

export const COMFORT = Object.freeze({
  /** Teleport only — GDD 12.3 forbids artificial locomotion in VR. */
  vrLocomotion: 'teleport',
  blinkOutTime: 0.12,
  blinkHoldTime: 0.05,
  blinkInTime: 0.18,
  vignetteStrength: 0.65,
  vignetteFadeTime: 0.25,
  /** Max teleport arc distance, metres. */
  teleportMaxRange: 12,
  /** Thumbstick deflection that arms the teleport arc. */
  teleportDeadzone: 0.6,
});

export const HAND = Object.freeze({
  /** Desktop hand rest offset from the camera, in camera space. */
  desktopOffset: { x: 0.28, y: -0.28, z: -0.45 },
  /** Sphere collider radius for a wing-hand. */
  radius: 0.09,
  /**
   * Grab search radius around the hand point. Generous on purpose: a desktop
   * hand is pinned to the camera and cannot reach, so a tight radius makes
   * dispensed objects impossible to pick up without VR.
   */
  grabRadius: 0.45,
});

// ─────────────────────────────────────────────────────────────────────────────
// Tin Can Alley (GDD 6.2.1) — the exemplar stall
// ─────────────────────────────────────────────────────────────────────────────

export const TIN_CAN_ALLEY = Object.freeze({
  cost: 3,
  ballsPerRound: 3,
  canMass: 0.3,
  canFriction: 0.15,
  canRestitution: 0.25,
  canRadius: 0.045,
  canHeight: 0.12,
  ballRadius: 0.037,
  ballMass: 0.145,

  /** Shelf height above the play floor. */
  shelfHeight: 1.05,
  /** A can is "off the shelf" once its centre drops this far below the shelf. */
  offShelfDrop: 0.14,
  /** …or once it leaves this lateral half-extent around its pyramid. */
  offShelfRadius: 0.85,

  /**
   * `x` staggers the STACKS laterally so the three pyramids stop overlapping in
   * perspective and read as three targets rather than one clump. Stall-local +X
   * is the player's LEFT, so near sits left, mid stays on the centre line and
   * far sits right.
   *
   * The SHELVES do not move — every shelf, its collider and its z are untouched.
   * That is deliberate on two counts:
   *   • the mid pyramid is what a centred scripted throw actually hits, so it
   *     has to stay at x = 0 or the throw tests stop scoring;
   *   • the toppled-can test lays cans at x = −0.25 … +0.25 on the NEAR shelf
   *     and expects them to rest there, so that plank must keep spanning ±0.38.
   * The offsets are therefore capped by each plank's own half-width minus the
   * stack's, leaving ≥ 6 cm of plank under the outermost can.
   */
  pyramids: Object.freeze([
    { key: 'near', cans: 3,  distance: 2.5, x:  0.22, pointsPerCan: 10, clearBonus: 50,  rows: 2, directPrize: null },
    { key: 'mid',  cans: 6,  distance: 3.5, x:  0,    pointsPerCan: 20, clearBonus: 150, rows: 3, directPrize: 'plush-small' },
    { key: 'far',  cans: 10, distance: 4.5, x: -0.35, pointsPerCan: 30, clearBonus: 500, rows: 4, directPrize: 'plush-large' },
  ]),

  /** Seconds after the last ball lands before the round auto-ends. */
  settleTime: 2.2,
  /** Seconds a thrown ball may live before it is recycled. */
  ballLifetime: 8,
});

// ─────────────────────────────────────────────────────────────────────────────
// Save schema (see save.js)
// ─────────────────────────────────────────────────────────────────────────────

export const SAVE_KEY = 'carnival-chaos-v2';
export const SAVE_SCHEMA_VERSION = 2;
/** Debounce window before an autosave hits localStorage. */
export const SAVE_DEBOUNCE_MS = 400;

// ─────────────────────────────────────────────────────────────────────────────
// Event names
//
// Canonical strings live here so a stall agent can emit an event without
// importing the bus's private table. events.js re-exports this as `Events`.
// ─────────────────────────────────────────────────────────────────────────────

export const EVENTS = Object.freeze({
  // Engine / session
  /**
   * CAUTION: emitted TWICE — once by `engine.init()` with `{ engine }` before
   * the world exists, and again at the end of boot with `{ version }`. Do not
   * self-install on this. Use WORLD_READY.
   */
  ENGINE_READY: 'engine:ready',
  /**
   * Emitted exactly once, after hub, avatar, teleport, stalls and stubs are all
   * built. THE event for a module that installs itself into the world.
   * Payload: `{ engine, scene, hub, version }`.
   */
  WORLD_READY: 'engine:world-ready',
  FRAME: 'engine:frame',
  RESIZE: 'engine:resize',
  XR_SESSION_START: 'xr:session-start',
  XR_SESSION_END: 'xr:session-end',
  XR_SUPPORTED: 'xr:supported',

  // Input
  INPUT_GRIP_DOWN: 'input:grip-down',
  INPUT_GRIP_UP: 'input:grip-up',
  INPUT_TRIGGER_DOWN: 'input:trigger-down',
  INPUT_TRIGGER_UP: 'input:trigger-up',
  INPUT_THUMBSTICK: 'input:thumbstick',
  INPUT_ACTION: 'input:action',

  // Grab / throw
  GRAB: 'grab:grab',
  RELEASE: 'grab:release',
  THROW: 'grab:throw',

  // Physics
  CONTACT_FORCE: 'physics:contact-force',
  COLLISION: 'physics:collision',

  // Avatar
  AVATAR_MODE: 'avatar:mode',
  TELEPORT: 'avatar:teleport',

  // Stalls
  STALL_APPROACH: 'stall:approach',
  STALL_LEAVE: 'stall:leave',
  STALL_ACTIVATE: 'stall:activate',
  STALL_ACTIVATE_FAILED: 'stall:activate-failed',
  STALL_ROUND_START: 'stall:round-start',
  STALL_SCORE: 'stall:score',
  STALL_ATTEMPT: 'stall:attempt',
  STALL_ROUND_END: 'stall:round-end',
  STALL_EXIT: 'stall:exit',
  STALL_REGISTERED: 'stall:registered',

  // Economy / meta
  WALLET_CHANGE: 'economy:wallet-change',
  TICKETS_SPENT: 'economy:tickets-spent',
  TOKENS_AWARDED: 'economy:tokens-awarded',
  DIRECT_PRIZE: 'economy:direct-prize',
  INSUFFICIENT_TICKETS: 'economy:insufficient-tickets',
  SAVE_WRITE: 'save:write',
  SAVE_LOAD: 'save:load',
  SAVE_RESET: 'save:reset',

  // UI
  UI_PROMPT: 'ui:prompt',
  UI_TOAST: 'ui:toast',
  UI_PAYOUT: 'ui:payout',
  UI_SCORE_POPUP: 'ui:score-popup',

  // Audio (stub module consumes these)
  AUDIO_PLAY: 'audio:play',
});

// ─────────────────────────────────────────────────────────────────────────────
// Direct prizes (GDD 7.4)
// ─────────────────────────────────────────────────────────────────────────────

export const PRIZE_KIND = Object.freeze({
  PLUSH_SMALL: 'plush-small',
  PLUSH_LARGE: 'plush-large',
  FISH: 'fish',
});

export const FISH_VARIANTS = Object.freeze([
  { id: 'orange',  name: 'Common Orange', rarity: 'common',   color: '#ff8c42' },
  { id: 'spotted', name: 'Spotted',       rarity: 'uncommon', color: '#e8d5a3' },
  { id: 'golden',  name: 'Golden',        rarity: 'rare',     color: '#ffd700' },
]);

// ─────────────────────────────────────────────────────────────────────────────
// Palette — shared so every module's greybox reads as one carnival
// ─────────────────────────────────────────────────────────────────────────────

export const PALETTE = Object.freeze({
  skyTop: 0x1b2a5e,
  skyBottom: 0xf5a35c,
  ground: 0x4a5a3c,
  path: 0xb9a689,
  boothCanvas: 0xf4f1e8,
  boothFrame: 0x8a5a3b,
  stripeA: 0xe8503a,
  stripeB: 0xf4f1e8,
  gooseBody: 0xf2f2ee,
  gooseBeak: 0xf5a623,
  gooseFeet: 0xf5a623,
  wing: 0xe8e8e2,
  metal: 0x9aa3ad,
  wood: 0x8d5524,
  gold: 0xd4af37,
  token: 0x6fe7c4,
  ticket: 0xffd166,
  star: 0xcba6ff,
  lampWarm: 0xffd9a0,
  signBg: 0x1b2340,
  signText: '#ffd166',
});

// ─────────────────────────────────────────────────────────────────────────────
// Duck Shooting Gallery (GDD 6.5.1)
//
// Three conveyor rows of metal duck silhouettes running at 0.5 / 1.2 / 2.0 m/s
// in alternating directions, tiered so the far row clears the near one. Rows
// are authored by the CENTRE height of a duck; the stall offsets the factory
// mesh (whose origin sits at its feet) to match.
// ─────────────────────────────────────────────────────────────────────────────

export const DUCK_GALLERY = Object.freeze({
  cost: 5,
  /** GDD 6.5.1: 60-second timed round, unlimited shots. */
  roundSeconds: 60,
  bodyPoints: 50,
  bullseyePoints: 200,
  goldenPoints: 1000,

  /** Minimum seconds between shots — roughly three per second. */
  fireInterval: 1 / 3,
  /** Cork rifle raycast reach, metres. */
  shotRange: 16,
  /** Muzzle flash lifetime, seconds. */
  flashTime: 0.07,

  /** Half-length of a conveyor run in stall-local X. Booth width is 5 m. */
  trackHalf: 2.15,
  /** Seconds the knock-flat animation takes. */
  knockTime: 0.28,
  /** Seconds a duck stays down before it pops back up (GDD 6.5.1). */
  respawnDelay: 1.5,

  /** Conveyor rows, near to far. `dir` alternates so the rows cross. */
  rows: Object.freeze([
    { key: 'front', y: 1.15, z: 3.20, speed: 0.5, dir: 1,  count: 4 },
    { key: 'mid',   y: 1.62, z: 4.10, speed: 1.2, dir: -1, count: 5 },
    { key: 'back',  y: 2.09, z: 5.00, speed: 2.0, dir: 1,  count: 5 },
  ]),

  /** The rare golden duck (GDD 6.5.1: 1000 pts, visible for 2 s). */
  golden: Object.freeze({
    minPerRound: 2,
    maxPerRound: 3,
    /** Exactly how long one stays up, seconds. */
    visibleSeconds: 2,
    /** The first appearance lands inside this window, so every round shows one. */
    firstWindow: Object.freeze([4, 7]),
    /** Later appearances are spread across the remainder of the round. */
    laterWindow: Object.freeze([14, 52]),
    speed: 2.4,
    /** Its own rail above the three rows, so no row duck can screen the shot. */
    y: 2.50,
    z: 4.30,
  }),
});

// ─────────────────────────────────────────────────────────────────────────────
// Dart Balloons (GDD 6.5.2)
//
// A 5 × 4 wall of balloons, 5 darts. The colour layout is drawn from the seeded
// RNG and always contains at least one horizontal run of three, so the combo
// bonus is reachable in every round.
// ─────────────────────────────────────────────────────────────────────────────

export const DART_BALLOONS = Object.freeze({
  cost: 3,
  dartsPerRound: 5,
  standardPoints: 50,
  /** GDD 6.5.2: three of a colour adjacent in a row. */
  comboBonus: 200,
  comboRun: 3,
  prizePoints: 500,

  cols: 5,
  rows: 4,
  colSpacing: 0.42,
  rowSpacing: 0.40,
  /** Centre height of the bottom row, stall-local Y. */
  baseY: 1.05,
  /** Backboard face, stall-local Z — where missed darts stick. */
  boardZ: 3.05,
  /** How far in front of the board a balloon floats. */
  balloonStandoff: 0.10,

  colors: Object.freeze([
    { key: 'red',    hex: 0xe8503a },
    { key: 'blue',   hex: 0x3aa6e8 },
    { key: 'yellow', hex: 0xffd166 },
    { key: 'green',  hex: 0x5ac18e },
  ]),
  prizeColor: 0xcba6ff,

  /** Pop effect (GDD 6.5.2: particle burst + confetti). */
  confettiCount: 14,
  confettiLife: 1.1,
  confettiSpeed: 1.9,

  /** Seconds after the last dart lands before the round closes. */
  settleTime: 2.4,
  /** A dart that never stuck is recycled after this long. */
  dartLifetime: 6,
});

// ─────────────────────────────────────────────────────────────────────────────
// Skee-Ball (GDD 6.4.2)
//
// Flat lane → jump ramp → target apron with concentric ring pockets. The round
// is 60 seconds with unlimited balls, so the dispenser keeps `maxLiveBalls` in
// the tray and scored or dead balls are despawned.
//
// "Must be rolled, not thrown" is enforced at release: too high above the lane
// deck, or pitched too far up, and the ball is a foul that cannot score.
// ─────────────────────────────────────────────────────────────────────────────

export const SKEE_BALL = Object.freeze({
  cost: 3,
  roundSeconds: 60,
  /** Balls the dispenser keeps within reach at once. */
  maxLiveBalls: 3,
  /**
   * The ball return, beside the lane rather than on it. On the lane the balls
   * sat below the bottom of the frame — invisible, and past the 0.45 m desktop
   * grab reach. Beside it at hand height they are both seen and reachable, and
   * still low enough that picking one up is not already a foul.
   *
   * `x` is NEGATIVE because stall-local +X is the player's LEFT, not their
   * right. A yaw of θ puts local +X at world (cos θ, 0, −sin θ) while the
   * camera's right is world (cos θ', 0, −sin θ') for θ' = θ + π — the two are
   * opposite, on both rows. The desktop right hand therefore sits at local
   * x ≈ −0.28, which is the side the dispenser has to be on.
   */
  rack: Object.freeze({ x: -0.50, y: 1.24, z: 0.72, spacing: 0.14, width: 0.54 }),

  /** GDD 6.4.2: lane friction 0.3 — the ball must keep its momentum. */
  laneFriction: 0.3,
  /** Lane deck: flat, at hand height so a roll is a roll and not a drop. */
  laneY: 1.02,
  laneZStart: 0.30,
  laneZEnd: 2.70,
  laneWidth: 0.66,
  /** The jump at the far end of the lane. */
  rampZEnd: 3.20,
  rampTopY: 1.30,

  /** Target apron the ball hops onto. */
  deckY: 1.16,
  deckZStart: 3.46,
  deckZEnd: 5.30,
  deckWidth: 1.58,
  /** Centre of the concentric rings, stall-local Z. */
  ringCentreZ: 4.55,

  /** Concentric pockets, INNERMOST FIRST — the first ring that contains the
   *  resting ball is the one that scores (GDD 6.4.2). */
  rings: Object.freeze([
    { key: 'centre', radius: 0.085, points: 500, color: 0xd4af37 },
    { key: 'inner',  radius: 0.200, points: 100, color: 0x9b5de5 },
    { key: 'middle', radius: 0.360, points: 50,  color: 0x3aa6e8 },
    { key: 'outer',  radius: 0.620, points: 10,  color: 0x5ac18e },
  ]),

  /** Foul rule: release more than this above the lane deck. */
  maxReleaseHeight: 0.42,
  /** …or pitched further up than this. */
  maxReleasePitchDeg: 20,
  /** How much of the throw is returned as a gentle bounce-back. */
  foulBounceBack: 0.30,
  foulSignSeconds: 2.0,

  /** A ball in a pocket scores once it slows below this… */
  settleSpeed: 0.45,
  /** …or has been in the pocket this long. */
  pocketDwell: 1.4,
  /** Thrown balls are recycled after this long. */
  ballLifetime: 9,
  /** Ball-return chute animation, seconds. */
  returnTime: 0.9,
});

// ─────────────────────────────────────────────────────────────────────────────
// Ring Toss (GDD 6.3.1)
//
// The ring must encircle a bottle neck. Our greybox ring is a solid cylinder
// collider rather than an annulus, so it cannot physically drop over a neck.
// The bottle's geometry is what makes the rule honest instead: the solid part
// of a bottle narrows to `capRadius` at the top, and `capRadius` is exactly
// `ringInnerRadius - neckRadius` — the largest off-centre distance at which a
// real ring would still encircle the neck. A disc balanced on that cap is
// therefore in a position a real ring could hold, and one that lands anywhere
// wider hits the shoulder and slides off, which is the miss the GDD wants.
// ─────────────────────────────────────────────────────────────────────────────

export const RING_TOSS = Object.freeze({
  cost: 5,
  ringsPerRound: 5,

  /** Factory createRing: 8 cm inner diameter (GDD 6.3.1). */
  ringInnerRadius: 0.04,
  /** Outer half-extent of the ring's cylinder collider. */
  ringOuterRadius: 0.058,
  ringHalfHeight: 0.009,
  ringMass: 0.03,
  /** GDD 5.4: floaty, arc-dependent. */
  ringGravityScale: 0.8,
  /** GDD 6.3.1 "Ring drag coefficient 0.6". */
  ringLinearDamping: 0.6,
  ringAngularDamping: 0.5,
  ringRestitution: 0.22,
  ringColors: Object.freeze([0x3aa6e8, 0xe8503a, 0xffd166, 0x6fe7c4, 0x9b5de5]),

  /** Height of the bottle table above the play floor. */
  tableHeight: 0.75,
  tableWidth: 2.4,
  /** Table spans z ∈ [tableZ − tableHalfDepth, tableZ + tableHalfDepth]. */
  tableZ: 3.0,
  tableHalfDepth: 1.6,

  /** Factory createBottle geometry, relative to the bottle's base. */
  bottleNeckY: 0.35,
  bottleNeckRadius: 0.0175,
  bottleBodyRadius: 0.045,
  /** Solid glass body: 0 → bodyTopY. */
  bottleBodyTopY: 0.20,
  /** Narrow catch above the body: bodyTopY → capTopY, radius capRadius. */
  bottleCapTopY: 0.29,
  /** ringInnerRadius − bottleNeckRadius. See the note above. */
  bottleCapRadius: 0.0225,

  /**
   * A landed ring's centre must sit within this of the bottle axis in XZ.
   * 1 mm of solver slack over capRadius.
   */
  landRadius: 0.0235,
  /** Ring centre height above the bottle base, min and max, for a landing. */
  landMinY: 0.21,
  /** …and it must rest BELOW the top of the neck (GDD 6.3.1). */
  landMaxY: 0.35,
  /** Where a landed ring is snapped to, so it reads as sitting on the neck. */
  landSnapY: 0.30,
  /** A ring more tilted than this is not "landed cleanly over" anything. */
  landMaxTiltDeg: 35,

  /** Ring is at rest below these, held for restTime seconds. */
  restSpeed: 0.35,
  restAngular: 2.2,
  restTime: 0.22,

  rows: Object.freeze([
    { key: 'near', distance: 2.0, points: 50,  bottles: 5, spacing: 0.24, directPrize: null },
    { key: 'mid',  distance: 3.0, points: 150, bottles: 4, spacing: 0.28, directPrize: 'plush-small' },
    { key: 'far',  distance: 4.0, points: 500, bottles: 3, spacing: 0.34, directPrize: 'plush-large' },
  ]),

  /** Seconds after the last ring resolves before the round auto-ends. */
  settleTime: 2.4,
  /** A ring that has not come to rest by now is resolved where it lies. */
  ringLifetime: 9,
});

// ─────────────────────────────────────────────────────────────────────────────
// Ping Pong Goldfish (GDD 6.3.2)
//
// The load-bearing rule is the bounce: a ball must touch the table before it
// enters a bowl, so a direct lob is refused and popped back out. Bowls are
// trimesh shells rather than solid cylinders, which is what lets a ball fall
// inside one and rattle against its walls.
// ─────────────────────────────────────────────────────────────────────────────

export const PING_PONG_GOLDFISH = Object.freeze({
  cost: 5,
  ballsPerRound: 5,

  /** Factory createBall('pingpong') — GDD 6.3.2 restitution 0.75. */
  ballRadius: 0.02,
  ballMass: 0.003,
  ballRestitution: 0.75,
  /** GDD 5.4: very light, bounce physics critical. */
  ballGravityScale: 0.6,

  tableHeight: 0.90,
  tableWidth: 2.2,
  tableZ: 2.4,
  tableHalfDepth: 1.25,
  /** Bouncy enough to make the required bounce reliable, not trampoline-like. */
  tableRestitution: 0.62,
  tableFriction: 0.35,

  /** Bowl rows, front to back. Smaller bowls score higher (GDD 6.3.2). */
  rows: Object.freeze([
    { size: 'large',  points: 50,  variant: 'orange',  z: 1.95, xs: Object.freeze([-0.52, 0, 0.52]) },
    { size: 'medium', points: 150, variant: 'spotted', z: 2.55, xs: Object.freeze([-0.30, 0.30]) },
    { size: 'small',  points: 500, variant: 'golden',  z: 3.10, xs: Object.freeze([0]) },
  ]),

  /** Velocity given to a ball that entered a bowl without bouncing first. */
  popOutUp: 2.2,
  popOutLateral: 1.1,
  /** A popped ball ignores bowl entry for this long, so it clears the mouth. */
  popOutCooldown: 0.45,

  /**
   * Velocity retained when a ball first breaks the water in a bowl. The bowls
   * hold water (GDD 6.3.2) and a 3 g ball entering water keeps almost none of
   * its energy — without this a legal landing pings around inside the glass
   * and hops back out, which reads as the game refusing a good throw.
   */
  waterDamping: 0.25,

  /** Ball is settled in a bowl below this speed, held for restTime. */
  restSpeed: 0.22,
  restTime: 0.25,

  splashTime: 0.55,
  splashRadius: 1.9,

  settleTime: 2.4,
  ballLifetime: 9,
});

// ─────────────────────────────────────────────────────────────────────────────
// Basketball Shoot (GDD 6.4.1)
//
// Hoops are staggered laterally as well as in depth so each one has a clear
// line from the counter — three rims in a single file would leave the far hoop
// permanently screened by the near one.
// ─────────────────────────────────────────────────────────────────────────────

export const BASKETBALL_SHOOT = Object.freeze({
  cost: 5,
  ballsPerRound: 3,

  /** Factory createBall('basketball'). */
  ballRadius: 0.108,
  ballMass: 0.60,
  ballRestitution: 0.60,

  /** GDD 6.4.1: 90% of a regulation 45.7 cm rim → 41.1 cm diameter. */
  rimRadius: 0.2055,
  /** Matches the tube the factory draws, so the collider is the rim you see. */
  rimTube: 0.016,
  rimHeight: 2.45,
  /** GDD 6.4.1 "Spin influence 0.3x on rim bounce" — backspin rewards skill. */
  spinInfluence: 0.3,

  backboard: Object.freeze({ width: 1.05, height: 0.7, thickness: 0.04, y: 0.35, z: 0.185 }),

  hoops: Object.freeze([
    { key: 'near', x: -0.95, z: 2.5, points: 100,  swishOnly: false },
    { key: 'mid',  x:  0.00, z: 3.5, points: 250,  swishOnly: false },
    { key: 'far',  x:  0.95, z: 4.5, points: 1000, swishOnly: true },
  ]),

  settleTime: 2.2,
  ballLifetime: 8,
});

// ─────────────────────────────────────────────────────────────────────────────
// Meta economy — prize sets, cosmetics, challenges, leaderboards, store
// (GDD 3.4, 7.5, 7.6, 8.3, 8.5, 8.7, 12.4). Owned by the meta agent.
//
// Piece costs ESCALATE within every set (GDD 8.5 "completion drive") and each
// ramp sums EXACTLY to that set's `totalTokens` in PRIZE_SETS above. The suite
// asserts that equality, so a piece cost cannot be edited in isolation.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} SetPiece
 * @property {string} id
 * @property {string} name
 * @property {number} cost Redemption tokens.
 */

/** setId → ordered pieces, cheapest first. @type {Readonly<Record<string, SetPiece[]>>} */
export const PRIZE_SET_PIECES = Object.freeze({
  // 5 pieces · 60+80+100+120+140 = 500
  'carnival-classic': Object.freeze([
    { id: 'carnival-classic-p1', name: 'Candy Floss',    cost: 60 },
    { id: 'carnival-classic-p2', name: 'Toffee Apple',   cost: 80 },
    { id: 'carnival-classic-p3', name: 'Popcorn Bucket', cost: 100 },
    { id: 'carnival-classic-p4', name: 'Carousel Horse', cost: 120 },
    { id: 'carnival-classic-p5', name: 'Brass Bell',     cost: 140 },
  ]),
  // 6 pieces · 100+120+140+160+180+200 = 900
  'neon-nights': Object.freeze([
    { id: 'neon-nights-p1', name: 'Glow Stick',     cost: 100 },
    { id: 'neon-nights-p2', name: 'Neon Arrow',     cost: 120 },
    { id: 'neon-nights-p3', name: 'UV Balloon',     cost: 140 },
    { id: 'neon-nights-p4', name: 'Laser Pointer',  cost: 160 },
    { id: 'neon-nights-p5', name: 'Neon Flamingo',  cost: 180 },
    { id: 'neon-nights-p6', name: 'Electric Halo',  cost: 200 },
  ]),
  // 6 pieces · 100+120+140+160+180+200 = 900
  'big-top-circus': Object.freeze([
    { id: 'big-top-circus-p1', name: 'Circus Ticket',  cost: 100 },
    { id: 'big-top-circus-p2', name: 'Clown Nose',     cost: 120 },
    { id: 'big-top-circus-p3', name: 'Juggling Pins',  cost: 140 },
    { id: 'big-top-circus-p4', name: 'Trapeze Rope',   cost: 160 },
    { id: 'big-top-circus-p5', name: 'Tamer’s Whip', cost: 180 },
    { id: 'big-top-circus-p6', name: 'Big Top Banner', cost: 200 },
  ]),
  // 8 pieces · 100+125+150+175+200+225+250+275 = 1500
  'plush-paradise': Object.freeze([
    { id: 'plush-paradise-p1', name: 'Bunny Plush',   cost: 100 },
    { id: 'plush-paradise-p2', name: 'Duck Plush',    cost: 125 },
    { id: 'plush-paradise-p3', name: 'Bear Plush',    cost: 150 },
    { id: 'plush-paradise-p4', name: 'Frog Plush',    cost: 175 },
    { id: 'plush-paradise-p5', name: 'Otter Plush',   cost: 200 },
    { id: 'plush-paradise-p6', name: 'Panda Plush',   cost: 225 },
    { id: 'plush-paradise-p7', name: 'Dragon Plush',  cost: 250 },
    { id: 'plush-paradise-p8', name: 'Unicorn Plush', cost: 275 },
  ]),
  // 8 pieces · 155+200+245+290+335+380+425+470 = 2500
  'golden-goose': Object.freeze([
    { id: 'golden-goose-p1', name: 'Golden Egg',     cost: 155 },
    { id: 'golden-goose-p2', name: 'Golden Feather', cost: 200 },
    { id: 'golden-goose-p3', name: 'Golden Ticket',  cost: 245 },
    { id: 'golden-goose-p4', name: 'Golden Bell',    cost: 290 },
    { id: 'golden-goose-p5', name: 'Golden Crown',   cost: 335 },
    { id: 'golden-goose-p6', name: 'Golden Trophy',  cost: 380 },
    { id: 'golden-goose-p7', name: 'Golden Nest',    cost: 425 },
    { id: 'golden-goose-p8', name: 'Golden Statue',  cost: 470 },
  ]),
  // Seasonal · 110+135+155+175+200+225 = 1000
  'spooky-carnival': Object.freeze([
    { id: 'spooky-carnival-p1', name: 'Candy Skull',     cost: 110 },
    { id: 'spooky-carnival-p2', name: 'Bat Wing',        cost: 135 },
    { id: 'spooky-carnival-p3', name: 'Ghost Sheet',     cost: 155 },
    { id: 'spooky-carnival-p4', name: 'Witch Hat',       cost: 175 },
    { id: 'spooky-carnival-p5', name: 'Pumpkin Lantern', cost: 200 },
    { id: 'spooky-carnival-p6', name: 'Haunted Mirror',  cost: 225 },
  ]),
  'winter-wonderland': Object.freeze([
    { id: 'winter-wonderland-p1', name: 'Sled Bell',    cost: 110 },
    { id: 'winter-wonderland-p2', name: 'Ice Crystal',  cost: 135 },
    { id: 'winter-wonderland-p3', name: 'Pine Wreath',  cost: 155 },
    { id: 'winter-wonderland-p4', name: 'Frost Feather',cost: 175 },
    { id: 'winter-wonderland-p5', name: 'Snow Globe',   cost: 200 },
    { id: 'winter-wonderland-p6', name: 'Frozen Nest',  cost: 225 },
  ]),
  'summer-splash': Object.freeze([
    { id: 'summer-splash-p1', name: 'Seashell',        cost: 110 },
    { id: 'summer-splash-p2', name: 'Ice Lolly',       cost: 135 },
    { id: 'summer-splash-p3', name: 'Beach Ball',      cost: 155 },
    { id: 'summer-splash-p4', name: 'Sun Hat',         cost: 175 },
    { id: 'summer-splash-p5', name: 'Surf Board',      cost: 200 },
    { id: 'summer-splash-p6', name: 'Sandcastle Flag', cost: 225 },
  ]),
});

/**
 * Cosmetic catalog (GDD 3.4). `slot` matches avatar/cosmetics.js SLOTS.
 * `source` is 'set' (complete a prize set) or 'login' (30-day streak).
 *
 * @typedef {Object} CosmeticDef
 * @property {string} id
 * @property {string} name
 * @property {'head'|'body'|'beak'|'feet'|'aura'} slot
 * @property {'set'|'login'} source
 * @property {string} [setId] Which set unlocks it.
 * @property {string} [blurb]
 */
export const COSMETIC_CATALOG = Object.freeze([
  { id: 'carnival-barker-hat', name: 'Carnival Barker Hat', slot: 'head', source: 'set', setId: 'carnival-classic',   blurb: 'Step right up.' },
  { id: 'neon-goose-glow',     name: 'Neon Goose Glow',     slot: 'aura', source: 'set', setId: 'neon-nights',        blurb: 'Honk in ultraviolet.' },
  { id: 'ringmaster-outfit',   name: 'Ringmaster Outfit',   slot: 'body', source: 'set', setId: 'big-top-circus',     blurb: 'Master of the big top.' },
  { id: 'giant-plush-goose',   name: 'Giant Plush Goose',   slot: 'feet', source: 'set', setId: 'plush-paradise',     blurb: 'A pet that follows you.' },
  { id: 'golden-goose-skin',   name: 'Golden Goose Skin',   slot: 'body', source: 'set', setId: 'golden-goose',       blurb: 'Worth its weight.' },
  { id: 'haunted-goose-skin',  name: 'Haunted Goose Skin',  slot: 'body', source: 'set', setId: 'spooky-carnival',    blurb: 'Spectral and smug.' },
  { id: 'snow-goose-skin',     name: 'Snow Goose Skin',     slot: 'body', source: 'set', setId: 'winter-wonderland',  blurb: 'Frost-tipped feathers.' },
  { id: 'beach-goose-skin',    name: 'Beach Goose Skin',    slot: 'body', source: 'set', setId: 'summer-splash',      blurb: 'Sun-bleached and salty.' },
  { id: 'login-star-crown',    name: 'Star Crown',          slot: 'head', source: 'login', blurb: 'Thirty days running.' },
  { id: 'login-comet-aura',    name: 'Comet Trail',         slot: 'aura', source: 'login', blurb: 'You leave a mark.' },
  { id: 'login-medal-sash',    name: 'Champion Sash',       slot: 'body', source: 'login', blurb: 'Earned, not bought.' },
]);

/**
 * GDD 8.7 — the day-30 exclusive rotates so each month's streak pays out a
 * different cosmetic. Indexed by completed-calendar count, modulo length.
 */
export const MONTHLY_LOGIN_COSMETICS = Object.freeze([
  'login-star-crown', 'login-comet-aura', 'login-medal-sash',
]);

/**
 * Weekly challenge templates (GDD 8.2 — Stars come from weekly challenges).
 * Three are drawn per ISO week from a seed derived from the week itself, so
 * every player sees the same three and they change on Monday.
 *
 * `kind` selects the progress counter; `stallScoped` templates also draw a
 * stall. `scoreFrac` is the fraction of that stall's GDD 7.3 scoreMax used as
 * the target, so a challenge target is always plausible for its stall.
 *
 * @typedef {Object} ChallengeTemplate
 * @property {string} id
 * @property {'score'|'plush'|'fish'|'variety'|'rounds'|'tokens'|'stallPlays'} kind
 * @property {string} text `{n}` and `{stall}` are substituted.
 * @property {number} stars Reward, 1–2.
 * @property {number} [n] Fixed target.
 * @property {number} [scoreFrac] Target as a fraction of the stall's scoreMax.
 * @property {boolean} [stallScoped]
 */
export const CHALLENGE_POOL = Object.freeze([
  { id: 'sharp-shooter',  kind: 'score',      text: 'Score {n} at {stall}',      stars: 2, scoreFrac: 0.35, stallScoped: true },
  { id: 'plush-hunter',   kind: 'plush',      text: 'Win {n} plushies',          stars: 2, n: 2 },
  { id: 'variety-act',    kind: 'variety',    text: 'Play {n} different stalls', stars: 2, n: 5 },
  { id: 'fish-fancier',   kind: 'fish',       text: 'Catch {n} fish',            stars: 1, n: 4 },
  { id: 'season-ticket',  kind: 'rounds',     text: 'Play {n} rounds',           stars: 1, n: 8 },
  { id: 'token-grind',    kind: 'tokens',     text: 'Earn {n} tokens',           stars: 2, n: 150 },
  { id: 'stall-regular',  kind: 'stallPlays', text: 'Play {stall} {n} times',    stars: 1, n: 3, stallScoped: true },
]);

/** How many challenges are live in any given week (GDD 8.2). */
export const CHALLENGE_COUNT = 3;

/**
 * GDD 7.6 — there is no backend here, so global rivals are simulated. Names are
 * drawn from this pool with a seed derived from stall + ISO week, which makes a
 * board stable all week and different every Monday.
 */
export const RIVAL_NAMES = Object.freeze([
  'HonkSolo', 'GooseTheLoose', 'QuackShot', 'FeatherWeight', 'WaddleDee',
  'GanderGrand', 'PlumeRaider', 'MotherGoose', 'BreadWinner', 'FlapJack',
  'WingCommander', 'GooseBumps', 'PreenQueen', 'SirHonksALot', 'DownyDave',
  'WebbedWonder', 'GaggleBoss', 'PondScholar', 'NestEgg', 'BeakerStreet',
  'MigrationKing', 'HonkeyTonk', 'TalonTed', 'CygnetSam',
]);

/** Simulated rivals per stall per board (GDD 7.6). Player makes the tenth row. */
export const RIVAL_COUNT = 9;

/** Rival scores span this fraction of a stall's GDD 7.3 score range. */
export const RIVAL_SCORE_BAND = Object.freeze({ low: 0.22, high: 1.02 });

/** Seconds a hub leaderboard board shows one stall before rotating (GDD 7.6). */
export const LEADERBOARD_ROTATE_SECONDS = 8;

/** Rows rendered on the physical hub boards (they are small). */
export const LEADERBOARD_BOARD_ROWS = 6;

/**
 * GDD 12.4 — IAP presentation. The store is a DEMO: no payment is taken and
 * every surface says so. `save.settings.iapEnabled` hides the store entirely,
 * which is the age-rating requirement.
 */
export const STORE_CONFIG = Object.freeze({
  demoLabel: 'DEMO STORE — no real payment is taken',
  /** Where the kiosk stands, near the entrance gate (GDD 10.2). */
  kiosk: { x: -2.6, y: 0, z: 7.4, yaw: Math.PI * 0.18 },
});

/** Metres from a meta interaction point at which its prompt/overlay arms. */
export const META_INTERACT_RADIUS = 3.6;

// ─────────────────────────────────────────────────────────────────────────────
// Coconut Shy (GDD 6.2.2)
//
// The "power throw" stall. A coconut sits seated on its post until a contact
// dislodges it; anything short of that wobbles the coconut and scores nothing.
// ─────────────────────────────────────────────────────────────────────────────

export const COCONUT_SHY = Object.freeze({
  cost: 5,
  ballsPerRound: 3,
  coconutMass: 1.5,
  coconutRadius: 0.075,

  /** GDD 6.2.2: a contact below this force never dislodges a coconut. */
  knockoffForceN: 8,
  /**
   * The force rule alone does not express the design. Any real ball-on-coconut
   * contact is worth tens of newtons, so "force > 8 N" on its own would score
   * every touch — while GDD 6.2.2 explicitly wants a glancing blow to wobble
   * and not score. These two extra conditions are what make "cleanly knocked
   * off" mean something: the ball must still be travelling when it arrives, and
   * it must arrive roughly AT the coconut rather than brushing past it.
   */
  minKnockSpeed: 3.0,
  /** cos of the angle between the ball's heading and the line to the coconut. */
  minKnockDirectness: 0.35,

  /** Post heights and their point values (GDD 6.2.2: 100/200/300 by height). */
  posts: Object.freeze([
    { key: 'low',  height: 1.2, distance: 3.0, x: -0.78, points: 100 },
    { key: 'mid',  height: 1.5, distance: 3.5, x: 0.0,   points: 200 },
    { key: 'high', height: 1.8, distance: 4.0, x: 0.78,  points: 300 },
  ]),
  postRadius: 0.045,

  /** Damped-spring tilt applied to a coconut that was hit but not dislodged. */
  wobble: Object.freeze({
    maxTilt: 0.32,      // radians at the peak of a full-force near-miss
    decay: 4.0,         // e-folding rate
    frequency: 15.0,    // rad/s
    minTilt: 0.005,     // below this the wobble is considered finished
    lean: 0.55,         // how much of the tilt shows as lateral lean
  }),

  /** Extra impulse applied on knock-off so the coconut leaves properly. */
  knockImpulse: 2.6,
  /** Seconds after the last ball before the round closes. */
  settleTime: 2.4,
  ballLifetime: 8,
});

// ─────────────────────────────────────────────────────────────────────────────
// High Striker (GDD 6.6.1)
//
// Only the DOWNWARD component of the mallet head's velocity at the moment it
// reaches the pad counts. A sideways swipe across the pad is worth nothing,
// which is the whole anti-cheese rule in GDD 6.6.1.
// ─────────────────────────────────────────────────────────────────────────────

export const HIGH_STRIKER = Object.freeze({
  cost: 5,
  swings: 3,

  towerHeight: 2.2,
  /**
   * Distance from the player to the tower, and it is set by the CAMERA, not by
   * taste. First person is a 65° vertical FOV at eye height 1.62 with a −0.12
   * rad arrival pitch, so the frame reaches 0.480 above the eye line and 0.820
   * below it, per metre of depth; the booth counter's far lip then eats
   * everything steeper than 0.472 down.
   *
   * The tower has to fit BOTH ends between those:
   *   bell top   2.42 m, nearest face at towerZ − 0.18 → towerZ > 1.85
   *   lowest label bottom 0.51 m, at towerZ − 0.15    → towerZ > 2.51 (counter)
   *
   * At the old 1.62 the bell was cut off by the top of the frame and WEAKLING
   * and AVERAGE were swallowed by the counter. 3.0 clears the bell by ~117 px
   * and the bottom label by ~42 px at 1280×720, and still sits inside the ~7 m
   * booth interior. The pad is NOT derived from this — it stays at `padZ`,
   * within swinging reach of the counter.
   */
  towerZ: 3.0,
  towerWidth: 0.3,
  /** Lever pad, at roughly waist height in front of the player. */
  padY: 0.95,
  padZ: 0.86,
  padRadius: 0.26,
  padThickness: 0.07,
  /** Bell sits just above the top of the tower. */
  bellY: 2.32,

  /** Mallet head pose ring buffer — same estimator shape as GDD 5.3. */
  velocityBufferSize: 12,
  velocitySmoothFrames: 5,
  velocityWeightDecay: 0.72,
  /** Seconds before the pad can register another strike. */
  strikeCooldown: 0.4,

  /**
   * Zones in ascending order. A strike scores the highest zone whose
   * `minVelocity` its downward speed strictly exceeds; below the first zone the
   * swing registers but scores nothing (GDD 6.6.1 sideways-swing case).
   */
  zones: Object.freeze([
    { key: 'weakling', name: 'WEAKLING', minVelocity: 0.25, points: 50,   color: '#9aa3ad' },
    { key: 'average',  name: 'AVERAGE',  minVelocity: 1.2,  points: 100,  color: '#5ac18e' },
    { key: 'strong',   name: 'STRONG',   minVelocity: 2.2,  points: 250,  color: '#3aa6e8' },
    { key: 'mighty',   name: 'MIGHTY',   minVelocity: 3.2,  points: 500,  color: '#e8b23a' },
    { key: 'bell',     name: 'BELL!',    minVelocity: 4.0,  points: 1000, color: '#e8503a' },
  ]),
  /** GDD 6.6.1: the bell needs downward velocity > 4 m/s. */
  bellVelocity: 4.0,

  /** Downward velocity that sends the puck to the very top of the tower. */
  puckFullScaleVelocity: 4.0,
  puckRiseTime: 0.42,
  puckHangTime: 0.35,
  puckFallTime: 0.65,
  /** Seconds after the last swing settles before the round closes. */
  settleTime: 1.6,
  /** Mallet recovery: reseat it if it ends up further than this from rest. */
  malletLeashRadius: 2.6,
  /**
   * Stall-local +X is the player's LEFT, so the desktop RIGHT hand rests at
   * x ≈ −0.28. A single-handed tool therefore has to sit at NEGATIVE x or it
   * lands 0.62 m from the hand — outside the 0.45 m grab radius, which makes
   * the stall unplayable on desktop.
   */
  malletRest: Object.freeze({ x: -0.3, y: 1.12, z: 0.5 }),
});

// ─────────────────────────────────────────────────────────────────────────────
// Whack-a-Mole (GDD 6.6.2)
//
// 3×3 table, two mallets, one 60-second round. Mole visibility ramps linearly
// from 1.5 s at the start to 0.4 s at the end, so the finish is frantic.
// ─────────────────────────────────────────────────────────────────────────────

export const WHACK_A_MOLE = Object.freeze({
  cost: 3,
  roundSeconds: 60,

  /** 3×3 grid of holes, close enough that a desktop hand can reach the back row. */
  gridSize: 3,
  holeSpacing: 0.27,
  tableY: 1.0,
  tableZ: 0.9,
  tableWidth: 1.02,
  tableDepth: 1.02,
  holeRadius: 0.078,

  moleRadius: 0.062,
  moleHeight: 0.17,
  /** How far a mole's centre rises above / sinks below the table top. */
  moleRise: 0.1,
  moleSink: 0.19,
  /** Seconds for the pop-up and drop-down travel. */
  popTime: 0.11,

  /** GDD 6.6.2 visibility ramp across the round. */
  visibilityStart: 1.5,
  visibilityEnd: 0.4,
  goldenVisibility: 0.8,

  /** Type roll, in order: golden first, then bomb, else standard. */
  goldenChance: 0.12,
  bombChance: 0.18,

  /** Spawn cadence also tightens across the round. */
  spawnIntervalStart: 0.8,
  spawnIntervalEnd: 0.3,
  maxConcurrent: 3,

  points: Object.freeze({ standard: 50, golden: 200, bomb: -100 }),

  malletCount: 2,
  malletRest: Object.freeze({ x: 0.3, y: 1.14, z: 0.42 }),
  /** Seconds a mallet head is ignored after it lands a hit. */
  hitCooldown: 0.18,
  /** Squash animation length once a mole is bonked. */
  squashTime: 0.22,
  /** Mallet recovery leash, as for High Striker. */
  malletLeashRadius: 2.2,
});

// ─────────────────────────────────────────────────────────────────────────────
// Networking (GDD 10.1, 10.3, 10.4)
//
// The GDD specifies EOS Lobby + EOS P2P Relay. The web build substitutes PeerJS
// on the public broker — same star topology, same 10 Hz pose rate, same 16-goose
// cap. Everything here is best-effort: the carnival must feel alive with the
// broker unreachable, which is what the NPC table below is for.
// ─────────────────────────────────────────────────────────────────────────────

export const NET = Object.freeze({
  /** Bumped when a message shape changes incompatibly. Mismatches are dropped. */
  protocolVersion: 1,
  /** Namespaces our peer ids on the shared public broker. */
  idPrefix: 'cc2',
  /**
   * Deterministic public lobby ids, tried in order. Every client races to join
   * lobby 0 first; whoever finds it empty claims the id and becomes its host.
   */
  lobbies: Object.freeze(['cc2-lobby-0', 'cc2-lobby-1', 'cc2-lobby-2']),
  /** GDD 10.1 — 16 concurrent geese per instance. Joiners beyond cascade on. */
  maxPeers: 16,

  /** GDD 10.3 — hub position sync rate. */
  syncHz: 10,
  syncIntervalMs: 100,
  /** How long a join attempt waits before deciding the lobby has no host. */
  joinTimeoutMs: 5000,
  /** How long claiming a host id waits before deciding it is taken. */
  hostClaimTimeoutMs: 5000,
  /** Remote geese render this far in the past so packet jitter is invisible. */
  interpDelayMs: 200,
  /** A peer silent for this long is dropped. */
  peerTimeoutMs: 12_000,

  /** Inbound flood guard — messages per second per peer before dropping. */
  maxInboundPerSecond: 30,
  /** Remote positions are clamped into this box before anything reads them. */
  bounds: Object.freeze({ x: 60, yMin: -2, yMax: 20, z: 60 }),
  maxNameLength: 18,
  /** Longest serialised visit payload accepted, in characters. */
  maxVisitBytes: 64_000,

  /** Neighbour-lane replay (GDD 10.4). Offset is in stall-local ±X. */
  laneOffset: 2.0,
  maxReplayMeshes: 10,
  replayLifetime: 2.4,
  /** Height at which a replayed projectile is considered to have landed. */
  replayImpactY: 0.28,
  replayImpactDuration: 0.7,
  replayDebrisCount: 4,
});

/** Guestbook reactions a visitor may leave (GDD 9.4). */
export const VISIT_REACTIONS = Object.freeze(['honk', 'applause', 'wow']);

// ─────────────────────────────────────────────────────────────────────────────
// Ambient NPC geese
//
// Not in the GDD by name — they exist because GDD 10.5 ships "hub presence" at
// launch and a midway with one goose in it reads as broken. NPCs run online and
// offline alike, are purely kinematic (no bodies, no colliders, no griefing
// surface), and follow a plan drawn from the seeded RNG so tests are stable.
// ─────────────────────────────────────────────────────────────────────────────

export const NPC = Object.freeze({
  count: 4,
  /** Ambient stroll — slower than the player's 3.2 m/s sprint, same bob feel. */
  speed: 1.75,
  turnRate: 3.4,
  arriveRadius: 0.4,
  /** Seconds spent "watching" at a waypoint. */
  pauseMin: 5,
  pauseMax: 15,
  bobAmplitude: 0.05,
  bobFrequency: 6.4,
  /** Odds, per arrival, of a honk and of an emote-hop. */
  honkChance: 0.45,
  hopChance: 0.32,
  hopHeight: 0.24,
  hopTime: 0.5,
  /** Seconds the HONK! speech bubble stays up. */
  bubbleSeconds: 1.0,
  /** Waypoints pre-drawn per goose. Cycles once exhausted. */
  planLength: 24,
  /**
   * Metres an NPC keeps between itself and a stall's play origin. Well over the
   * 1 m brief because a goose standing on the spot you activate from reads as a
   * blocker even when it has no collider.
   */
  stallClearance: 1.5,
  /** Height of the billboarded name tag above a goose's feet. */
  nameTagHeight: 1.5,
});

/**
 * Meta-layer event names.
 *
 * Kept separate from `EVENTS` rather than appended to it: `EVENTS` sits in the
 * middle of this file and several agents build against it in parallel, so
 * growing it is a merge conflict waiting to happen. The bus takes any string,
 * and these are namespaced `meta:` so they never collide.
 */
export const META_EVENTS = Object.freeze({
  SET_COMPLETE: 'meta:set-complete',
  PIECE_BOUGHT: 'meta:piece-bought',
  DAILY_CLAIMED: 'meta:daily-claimed',
  CHALLENGE_COMPLETE: 'meta:challenge-complete',
  STORE_PURCHASE: 'meta:store-purchase',
  COSMETIC_EQUIPPED: 'meta:cosmetic-equipped',
  DESK_OPEN: 'meta:desk-open',
  DESK_CLOSE: 'meta:desk-close',
});

// ─────────────────────────────────────────────────────────────────────────────
// Hook a Duck (GDD 6.7.1)
//
// A circular water channel with rubber ducks drifting round it on the current.
// The rod's hook hangs from a short pendulum the stall integrates itself — joint
// physics for a 3-link chain is both dearer and less stable than four passes of
// distance relaxation, and this way the catch test is exact rather than
// solver-dependent.
// ─────────────────────────────────────────────────────────────────────────────

export const HOOK_A_DUCK = Object.freeze({
  cost: 3,
  seconds: 60,

  /**
   * Centre of the channel in stall-local space; y is the water surface.
   *
   * Waist-high and close, which is where a real duck pond sits — and, less
   * obviously, where it has to be to be VISIBLE. The player's eye is at 1.62,
   * so a tub at 0.86 lies ~25° below the horizon: right at the bottom edge of
   * the frame, which put the whole pond off-screen at neutral pitch. At 1.10 it
   * sits ~18° down and reads immediately. It also lines the ring anchors up
   * with where the hook naturally hangs, so a neutral pose is already close to
   * a catch.
   */
  ringCentre: Object.freeze({ x: 0, y: 1.1, z: 1.55 }),
  /** Radius of the duck path. Sized so the whole ring is inside rod reach. */
  ringRadius: 0.4,
  channelWidth: 0.34,
  /** GDD 6.7.1 — gentle, trackable current. */
  currentSpeed: 0.3,
  bobAmplitude: 0.012,
  bobFrequency: 1.6,

  /** Duck population. Points per size come straight from the GDD table. */
  ducks: Object.freeze([
    { size: 'large',  count: 5, points: 50 },
    { size: 'small',  count: 3, points: 200 },
    { size: 'golden', count: 1, points: 500 },
  ]),

  /** Hook pendulum hanging from the rod tip. */
  lineLength: 0.28,
  lineSegments: 3,
  /** Verlet velocity retention per step — under 1 or the line never settles. */
  lineDamping: 0.94,
  /** Distance-constraint relaxation passes per step. */
  lineIterations: 4,

  /** Added to a duck's ring radius so the hook has believable thickness. */
  hookTolerance: 0.025,
  /**
   * Vertical window in which the hook counts as through the ring, measured
   * from the ring anchor. Deliberately lopsided: a hook sitting BELOW the ring
   * and rising is exactly how a duck gets caught, and looking down at the water
   * pitches the rod tip — and so the hook — well under the ring. A symmetric
   * window punishes the camera angle players naturally adopt.
   */
  catchHeightWindow: 0.14,
  catchDepthWindow: 0.26,
  /** Upward rod-tip speed (m/s) that counts as lifting. */
  liftSpeed: 0.25,
  /** Seconds a caught duck rides the hook before returning to the current. */
  liftTime: 1.0,

  /** GDD 7.4 — always at least one fish, plus one per N catches. */
  guaranteedFish: 1,
  catchesPerBonusFish: 3,
  /** Relative weights; golden lands at 10%. */
  fishVariants: Object.freeze([
    { id: 'orange',  weight: 65 },
    { id: 'spotted', weight: 25 },
    { id: 'golden',  weight: 10 },
  ]),

  /**
   * Where the rod waits between grabs, in stall-local space.
   *
   * NEGATIVE x on purpose: stall-local +X is the player's LEFT, so the desktop
   * right hand sits at x ≈ −0.28. At +0.3 the rod is 0.61 m from that hand —
   * outside the 0.45 m grab radius, which makes the stall unplayable.
   */
  rodRest: Object.freeze({ x: -0.26, y: 1.16, z: 0.5 }),
  /** Seconds before a released rod glides back to its holder. */
  rodReturnDelay: 0.5,
  rodReturnTime: 0.35,
});

// ─────────────────────────────────────────────────────────────────────────────
// Coin Pusher (GDD 6.7.2)
//
// Distances are stall-local: +Z runs into the booth, so the FRONT lip a coin
// falls off is the SMALLEST z on the field and the pusher sweeps toward it.
//
// Body budget is the binding constraint here, not gameplay. Every coin is a
// small cylinder with CCD off and sleeping allowed, counted coins are despawned
// the frame they cross the lip, and `maxCoinBodies` caps what can ever be live
// at once — see the notes in coin-pusher.js.
// ─────────────────────────────────────────────────────────────────────────────

export const COIN_PUSHER = Object.freeze({
  cost: 5,
  coinsPerRound: 100,

  /** GDD 6.7.2 round shape, plus a warm-up that settles the pre-loaded field. */
  warmupSeconds: 1,
  activeSeconds: 90,
  settleSeconds: 10,

  /**
   * Playfield. `fieldY` is the top surface coins rest on.
   *
   * The machine sits DEEP in the booth on purpose. The player's eye is at 1.62
   * and the tray is necessarily low, so a cabinet pulled up close puts the
   * whole playfield ~31° below the horizon — past the bottom of the frame, and
   * the first screenshot showed exactly that: a coin pusher you cannot see the
   * coins in. Pushing it back to ~1.9 m raises the field into the frame. The
   * player never needs to reach the cabinet; they only reach the dispenser, and
   * the chute carries the coin the rest of the way.
   */
  fieldY: 0.78,
  fieldHalfWidth: 0.3,
  /** Front lip: cross this toward the player and you are off the edge. */
  zFront: 1.5,
  zBack: 2.3,
  /** Front face of the cabinet, in front of the payout well. */
  cabinetFrontZ: 1.18,

  /** Oscillating pusher (GDD: 0.5 Hz, slow and hypnotic). */
  pusherWidth: 0.58,
  pusherHeight: 0.1,
  pusherDepth: 0.26,
  pusherHz: 0.5,
  pusherStroke: 0.22,
  /** Centre Z at the retracted extreme; it sweeps forward by `pusherStroke`. */
  pusherHomeZ: 2.14,

  /**
   * Dropping below this in front of `zFront` = counted off the front edge.
   * Set between the tray and the payout-well floor (0.5) so a coin is visibly
   * falling into the well when it counts, rather than blinking out on the lip.
   */
  countPlaneY: 0.62,
  /** Beyond this |x| the coin is in a side gutter: lost, never counted. */
  gutterHalfWidth: 0.34,
  /** Below this y anywhere: gone, despawn. */
  killPlaneY: 0.3,

  /** Pre-loaded field (GDD: "coins and prizes already on the tray"). */
  fieldCoins: 35,
  fieldTokens: 4,
  fieldGrid: Object.freeze({ cols: 9, spacingX: 0.058, spacingZ: 0.072, z0: 1.62 }),

  coinMass: 0.008,
  coinFriction: 0.4,
  coinRestitution: 0.05,

  /** Hard ceiling on live coin bodies. SwiftShader will not forgive more. */
  maxCoinBodies: 110,
  /** Seconds a coin must sit still behind the pusher before it is merged away. */
  strandedSeconds: 4,

  /**
   * Dispenser: one grabbable coin at a time. Negative x for the same reason as
   * the fishing rod — +X is the player's LEFT, and the desktop right hand is at
   * x ≈ −0.28. At +0.16 the coin sat 0.44 m away, inside the 0.45 m radius by
   * 8 mm, which is not a margin worth shipping.
   */
  dispenserDelay: 0.35,
  dispenserPos: Object.freeze({ x: -0.16, y: 1.3, z: 0.44 }),

  /** Slot guide: a polished chute that funnels a dropped coin onto the field. */
  chuteFriction: 0.12,
  chuteMouth: Object.freeze({ y: 1.28, z: 0.38 }),
  /**
   * The exit stops well short of the pusher on purpose: a coin leaves here at
   * ~2 m/s and flies another ~0.23 m before it lands, which puts it in the
   * band the pusher's face actually sweeps. Move this back and coins land on
   * top of the pusher instead of in front of it.
   */
  chuteExit: Object.freeze({ y: 0.94, z: 1.68 }),
  chuteHalfWidth: 0.26,
  /** Hanging baffle that stops an over-fast coin sailing onto the pusher. */
  baffleZ: 2.02,

  /** GDD 6.7.2 prize tiers, by coins off the front edge. */
  tiers: Object.freeze([
    { max: 39,       prize: 'plush-small', bonusTokens: 0,  label: 'Small Prize' },
    { max: 70,       prize: 'plush-small', bonusTokens: 15, label: 'Medium Prize' },
    { max: Infinity, prize: 'plush-large', bonusTokens: 0,  label: 'BIG PRIZE' },
  ]),
  /** Redemption tokens per prize token pushed off the edge. */
  tokenBonusEach: 5,
});

// ─────────────────────────────────────────────────────────────────────────────
// Personal space (GDD 9)
//
// The room sits at a far corner of the same ground plane as the hub rather than
// in its own scene: the hub is simply hidden while you are inside, so entering
// costs a teleport instead of a rebuild, and VR teleport arcs still land on a
// floor at y = 0.
//
// GDD 9 specifies the shelf and tank progressions but not their Star prices —
// the costs below are ours, scaled so a first upgrade is reachable in a session
// or two and the top tier is a long-term goal.
// ─────────────────────────────────────────────────────────────────────────────

export const SPACE = Object.freeze({
  /** World position of the player's room, clear of the midway and its trees. */
  origin: { x: 0, y: 0, z: 46 },
  /** The NPC showcase space, and the slot a networked visit renders into. */
  npcOrigin: { x: 26, y: 0, z: 46 },
  visitorOrigin: { x: -26, y: 0, z: 46 },

  /** Interior is roomSize × roomSize metres. */
  roomSize: 8,
  wallHeight: 3.2,

  /** GDD 9.2: twelve slots to start, six per additional unit. */
  shelfSlotsBase: 12,
  shelfSlotsPerUnit: 6,
  /** Bays the room has wall space for — the ceiling on expansion. */
  shelfBays: 5,

  /** GDD 9.4 keeps the twenty most recent reactions. */
  guestbookMax: 20,

  /** How close to a hub portal the prompt appears. */
  portalRadius: 2.6,
});

/** Stars for the 3rd, 4th and 5th shelf unit (+6 slots each). */
export const SHELF_EXPANSION_COSTS = Object.freeze([3, 5, 8]);

/**
 * GDD 9.3 tank progression. `capacity` is a DISPLAY limit — fish beyond it are
 * waitlisted, never deleted.
 */
export const TANK_TIERS = Object.freeze([
  { id: 'bowl',     capacity: 5,  cost: 0,  name: 'Goldfish Bowl', width: 0.52, height: 0.46, depth: 0.52, standHeight: 0.86, decorAnchors: 1 },
  { id: 'small',    capacity: 10, cost: 3,  name: 'Small Tank',    width: 1.10, height: 0.60, depth: 0.46, standHeight: 0.72, decorAnchors: 2 },
  { id: 'medium',   capacity: 20, cost: 6,  name: 'Medium Tank',   width: 1.80, height: 0.85, depth: 0.58, standHeight: 0.58, decorAnchors: 3 },
  { id: 'aquarium', capacity: 50, cost: 12, name: 'Wall Aquarium', width: 3.20, height: 1.40, depth: 0.68, standHeight: 0.55, decorAnchors: 5 },
]);

/** GDD 9.2 shelf styles. `emissive` is what sells the neon and the gold. */
export const SHELF_THEMES = Object.freeze([
  { id: 'wooden-fairground', name: 'Wooden Fairground', cost: 0, wood: 0x8d5524, trim: 0xb4713a, emissive: 0x000000 },
  { id: 'neon-arcade',       name: 'Neon Arcade',       cost: 4, wood: 0x241a3d, trim: 0x00e5ff, emissive: 0x00b8d4 },
  { id: 'golden-trophy',     name: 'Golden Trophy Case', cost: 8, wood: 0x4a3b12, trim: 0xd4af37, emissive: 0x6b5411 },
]);

/**
 * GDD 9.4 floor and wall themes. The seasonal pair is listed but locked, so a
 * player can see what a season brings without being able to buy it out of one.
 */
export const ROOM_THEMES = Object.freeze([
  { id: 'classic-wood', name: 'Classic Wood', cost: 0, floor: 0x7a5333, wall: 0xd9c8a9, trim: 0x8d5524, light: 0xffd9a0, locked: false },
  { id: 'neon-glow',    name: 'Neon Glow',    cost: 4, floor: 0x1a1430, wall: 0x2b1f52, trim: 0x00e5ff, light: 0x9b5de5, locked: false },
  { id: 'haunted',      name: 'Haunted',      cost: 6, floor: 0x24222a, wall: 0x35303f, trim: 0x7bc043, light: 0x8fd66b, locked: true, season: 'Q4' },
  { id: 'winter',       name: 'Winter',       cost: 6, floor: 0xdfe9f5, wall: 0xeaf2fb, trim: 0x8fd0ff, light: 0xcfe8ff, locked: true, season: 'Q1' },
]);

/** GDD 9.3/9.4 decoration catalogue. `target` picks which anchors accept it. */
export const SPACE_DECOR = Object.freeze([
  { id: 'coral',          name: 'Coral',          cost: 1, target: 'tank', color: 0xff6f91 },
  { id: 'plants',         name: 'Water Plants',   cost: 1, target: 'tank', color: 0x4caf50 },
  { id: 'castle',         name: 'Sunken Castle',  cost: 2, target: 'tank', color: 0x9aa3ad },
  { id: 'bubbler',        name: 'Bubbler',        cost: 2, target: 'tank', color: 0xbfe9ff },
  { id: 'treasure-chest', name: 'Treasure Chest', cost: 3, target: 'tank', color: 0xd4af37 },
  { id: 'fairy-lights',   name: 'Fairy Lights',   cost: 2, target: 'room', color: 0xffd9a0 },
  { id: 'neon-sign',      name: 'Neon Sign',      cost: 3, target: 'room', color: 0xff2ea6 },
  { id: 'spotlight',      name: 'Spotlight',      cost: 2, target: 'room', color: 0xffffff },
]);

/** GDD 9.4 background music. Ids only — the audio agent owns playback. */
export const SPACE_MUSIC = Object.freeze([
  { id: 'carnival-day',  name: 'Carnival Day' },
  { id: 'midway-nights', name: 'Midway Nights' },
  { id: 'gentle-waltz',  name: 'Gentle Waltz' },
  { id: 'arcade-glow',   name: 'Arcade Glow' },
]);

/** GDD 9.4 guestbook reactions. */
export const GUESTBOOK_REACTIONS = Object.freeze([
  { id: 'honk',     label: 'HONK',     emoji: '📣' },
  { id: 'applause', label: 'Applause', emoji: '👏' },
  { id: 'wow',      label: 'Wow',      emoji: '🤩' },
]);

/**
 * Suggested fish names (GDD 9.3). Goose and fish puns, because the prompt has
 * to be quicker to answer than to dismiss.
 */
export const FISH_NAME_SUGGESTIONS = Object.freeze(
  ('Honk Ferry|Gooseberry|Sir Fins-a-Lot|Bubbles McGoose|Finn Diesel|Swim Shady'
  + '|Goosetav|Gill-bert|Waddles|Captain Beak|Splashy|Honk Solo|Feather Fin'
  + '|Goldie Honk|Pondemonium|Marina|Bread Winner|Gandalf the Gander|Preen'
  + '|Puddles|Nibbles|Fishy McFishface|The Great Scaley|Beakon|Cygnet').split('|'),
);
